import {useQuery} from '@tanstack/react-query';

import {useApiClient} from '@/hooks';
import {useUserDetails} from '@/lib/auth';
import {readLocalStorageJSON, writeLocalStorageJSON} from '@/utils';

import {accountSettingsApi} from '../api';

import type {ApiResponse, UserProfileDTO} from '@sollapay/types';

export const accountSettingsKeys = {
  all: ['account-settings'] as const,
  profile: () => [...accountSettingsKeys.all, 'profile'] as const
};

/** Scoped per user — a different account on the same browser must never see a stale cached profile. */
function profileStorageKey(userId: string): string {
  return `sollapay:account-settings:profile:${userId}`;
}

/** Seeds the next load; call after any write that resolves a fresh profile, not just the fetch. */
export function persistUserProfile(userId: string | undefined, profile: UserProfileDTO): void {
  if (userId) writeLocalStorageJSON(profileStorageKey(userId), profile);
}

export function useUserProfileQuery() {
  const client = useApiClient();
  const {user} = useUserDetails();
  const userId = user?.sub;

  return useQuery({
    queryKey: accountSettingsKeys.profile(),
    queryFn: async () => {
      const response = await accountSettingsApi.fetchProfile(client);
      persistUserProfile(userId, response.data);
      return response;
    },
    select: response => response.data,
    // Avoids flashing the Auth0 ID-token claims on first render while this fetch is in flight.
    initialData: userId
      ? (): ApiResponse<UserProfileDTO> | undefined => {
          const cached = readLocalStorageJSON<UserProfileDTO>(profileStorageKey(userId));
          return cached ? {data: cached} : undefined;
        }
      : undefined,
    initialDataUpdatedAt: 0, // stale immediately, so it still revalidates in the background
    // Well under the 24h presigned avatar URL window minted server-side.
    staleTime: 1000 * 60 * 60 * 4
  });
}

/** Server data only — the seed's `initialDataUpdatedAt: 0` and failed fetches never advance `dataUpdatedAt`. */
export function useRequiresNameConfirmation() {
  const {data: profile, dataUpdatedAt} = useUserProfileQuery();

  return {profile, isRequired: dataUpdatedAt > 0 && profile?.isNameConfirmed === false};
}
